/**
 * Food detail modal for viewing base food details.
 */

import * as stylex from "@stylexjs/stylex";

import { getFoodGroupLabel } from "@animus/habit-modules/nutrition";
import { badge, button, layout, modal, spacing, text } from "@animus/primaris";

import type { NutritionFood } from "./types.ts";
import { styles } from "./styles.stylex.ts";

interface FoodDetailModalProps {
  food: NutritionFood;
  onClose: () => void;
}

function FoodDetailModal({ food, onClose }: FoodDetailModalProps) {
  const foodGroupLabel = getFoodGroupLabel(food.foodGroup);

  return (
    <div {...stylex.props(modal.overlay)}>
      <div {...stylex.props(modal.container, styles.wideModal)}>
        <div {...stylex.props(modal.header)}>
          <div>
            <h2 {...stylex.props(text.xl, text.bold, text.white)}>
              {food.name}
            </h2>
            <p {...stylex.props(text.sm, text.muted)}>{food.normalizedName}</p>
          </div>
          <button
            {...stylex.props(modal.close)}
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>

        <div {...stylex.props(styles.modalBody)}>
          {food.description && (
            <p {...stylex.props(text.muted, spacing.mb4)}>{food.description}</p>
          )}

          <div {...stylex.props(styles.detailSection)}>
            <h3
              {...stylex.props(
                text.sm,
                text.bold,
                text.muted,
                styles.detailLabel
              )}
            >
              Macros (per 100g)
            </h3>
            <div {...stylex.props(styles.macroGrid)}>
              <div {...stylex.props(styles.macroItem)}>
                <div {...stylex.props(styles.macroValue, text.white)}>
                  {food.calories}
                </div>
                <div {...stylex.props(styles.macroLabel, text.muted)}>
                  Calories
                </div>
              </div>
              <div {...stylex.props(styles.macroItem)}>
                <div {...stylex.props(styles.macroValue, text.white)}>
                  {food.protein}g
                </div>
                <div {...stylex.props(styles.macroLabel, text.muted)}>
                  Protein
                </div>
              </div>
              <div {...stylex.props(styles.macroItem)}>
                <div {...stylex.props(styles.macroValue, text.white)}>
                  {food.carbs}g
                </div>
                <div {...stylex.props(styles.macroLabel, text.muted)}>
                  Carbs
                </div>
              </div>
              <div {...stylex.props(styles.macroItem)}>
                <div {...stylex.props(styles.macroValue, text.white)}>
                  {food.fat}g
                </div>
                <div {...stylex.props(styles.macroLabel, text.muted)}>Fat</div>
              </div>
              {food.fiber !== null && (
                <div {...stylex.props(styles.macroItem)}>
                  <div {...stylex.props(styles.macroValue, text.white)}>
                    {food.fiber}g
                  </div>
                  <div {...stylex.props(styles.macroLabel, text.muted)}>
                    Fiber
                  </div>
                </div>
              )}
              {food.saturatedFat !== null && (
                <div {...stylex.props(styles.macroItem)}>
                  <div {...stylex.props(styles.macroValue, text.white)}>
                    {food.saturatedFat}g
                  </div>
                  <div {...stylex.props(styles.macroLabel, text.muted)}>
                    Sat. Fat
                  </div>
                </div>
              )}
            </div>
          </div>

          <div {...stylex.props(styles.detailGrid)}>
            <div {...stylex.props(styles.detailSection)}>
              <h3
                {...stylex.props(
                  text.sm,
                  text.bold,
                  text.muted,
                  styles.detailLabel
                )}
              >
                Food Group
              </h3>
              <span {...stylex.props(badge.base, badge.blue)}>
                {foodGroupLabel}
              </span>
            </div>

            <div {...stylex.props(styles.detailSection)}>
              <h3
                {...stylex.props(
                  text.sm,
                  text.bold,
                  text.muted,
                  styles.detailLabel
                )}
              >
                Status
              </h3>
              <span
                {...stylex.props(
                  badge.base,
                  food.enrichmentStatus === "verified"
                    ? badge.green
                    : badge.yellow
                )}
              >
                {food.enrichmentStatus}
              </span>
            </div>
          </div>

          <div {...stylex.props(styles.detailSection)}>
            <h3
              {...stylex.props(
                text.sm,
                text.bold,
                text.muted,
                styles.detailLabel
              )}
            >
              Tags
            </h3>
            <div
              {...stylex.props(layout.flexRow, styles.flexWrap, layout.gap2)}
            >
              {(food.tags ?? []).map((tag) => (
                <span {...stylex.props(badge.base, badge.neutral)} key={tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div {...stylex.props(styles.detailSection)}>
            <h3
              {...stylex.props(
                text.sm,
                text.bold,
                text.muted,
                styles.detailLabel
              )}
            >
              Created
            </h3>
            <p {...stylex.props(styles.detailValue, text.white)}>
              {new Date(food.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div {...stylex.props(modal.footer)}>
          <button
            {...stylex.props(button.base, button.secondary)}
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export { FoodDetailModal, type FoodDetailModalProps };
